package com.flowyride.core.map

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.gestures.detectDragGestures
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.DpOffset
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.flowyride.core.designsystem.theme.FlowyColors
import com.flowyride.core.domain.model.GeoPoint
import org.maplibre.compose.map.MapState
import org.maplibre.compose.overlay.MapOverlayScope
import org.maplibre.spatialk.geojson.Position

private val TouchTarget = 40.dp
private val DotSize = 22.dp
private val DraggedDotSize = 30.dp

/**
 * A marker the rider can drag. While dragging, it follows the pointer locally; the new
 * position is reported once, on release. It stays where it was dropped until [marker] moves there.
 */
@Composable
internal fun MapOverlayScope.DraggableMarker(
    marker: MapMarker,
    mapState: MapState,
    onMoved: (GeoPoint) -> Unit,
) {
    val currentMarker by rememberUpdatedState(marker)
    val currentOnMoved by rememberUpdatedState(onMoved)
    val density = LocalDensity.current
    // Reset once the new position comes back through the state.
    var dragged by remember(marker.point) { mutableStateOf<Position?>(null) }
    var isDragging by remember { mutableStateOf(false) }
    var pointer by remember { mutableStateOf(DpOffset.Zero) }

    Box(
        Modifier
            .placedAt(dragged ?: marker.point.toPosition())
            .size(TouchTarget)
            // Swallow taps: a tap on a marker must not also count as a tap on the map.
            .pointerInput(Unit) { detectTapGestures { } }
            .pointerInput(marker.draggable) {
                if (!marker.draggable) return@pointerInput
                detectDragGestures(
                    onDragStart = {
                        pointer = mapState.screenLocationFromPosition(currentMarker.point.toPosition()) ?: return@detectDragGestures
                        isDragging = true
                    },
                    onDrag = { change, amount ->
                        if (!isDragging) return@detectDragGestures
                        change.consume()
                        pointer = with(density) { DpOffset(pointer.x + amount.x.toDp(), pointer.y + amount.y.toDp()) }
                        mapState.positionFromScreenLocation(pointer)?.let { dragged = it }
                    },
                    onDragEnd = {
                        isDragging = false
                        dragged?.let { currentOnMoved(GeoPoint(it.latitude, normalizeLongitude(it.longitude))) }
                    },
                    onDragCancel = {
                        isDragging = false
                        dragged = null
                    },
                )
            },
        contentAlignment = Alignment.Center,
    ) {
        val size = if (isDragging) DraggedDotSize else DotSize
        Box(
            Modifier
                .size(size)
                .shadow(4.dp, CircleShape)
                .background(marker.kind.color, CircleShape)
                .border(3.dp, Color.White, CircleShape),
            contentAlignment = Alignment.Center,
        ) {
            marker.label?.let { Text(it, color = Color.White, fontSize = 11.sp, fontWeight = FontWeight.Bold) }
        }
    }
}

private val MapMarker.Kind.color: Color
    get() = when (this) {
        MapMarker.Kind.DEPARTURE -> FlowyColors.Departure
        MapMarker.Kind.STOP -> FlowyColors.Stop
        MapMarker.Kind.ARRIVAL -> FlowyColors.Arrival
    }

internal fun GeoPoint.toPosition() = Position(longitude = longitude, latitude = latitude)
